import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import LessonCard from '@/components/lesson/LessonCard';
import { CourseAdminCard } from '@/components/shared/CourseAdminCard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import {
  DragDropPlaceholder,
  DroppableList,
} from '@/components/ui/custom/dataInput/DragDrop';
import { Loading } from '@/components/ui/daisy';
import useCourseTreeNavigate from '@/data/hook/course-line/useCourseTreeNavigate';
import useLessons from '@/data/hook/lesson/useLessons';
import Lesson from '@/models/Lesson';
import Unit from '@/models/Unit';
import { mountKey } from '@/utils/courses';
import { ROUTES } from '@/utils/routes';
import { usePedWorkspaceStatus } from '@/data/context/PedWorkspaceContext';

type UnitCardProps = {
  unit: Unit;
  index: number;
  lessons: Lesson[];
  isLoadingLessons: boolean;
  isReordering: boolean;
  onDelete: (unitId: number) => void;
  isDeleting?: boolean;
  courseLineId: number;
  moduleId: number;
  slug: string;
  isDraggable?: boolean;
  isDragging?: boolean;
};

export default function UnitCard({
  unit,
  index,
  lessons,
  isLoadingLessons,
  isReordering,
  onDelete,
  isDraggable,
  isDragging,
  isDeleting,
  courseLineId,
  moduleId,
  slug,
}: UnitCardProps) {
  const { isLimitedEdition } = usePedWorkspaceStatus();
  const { navigate } = useCourseTreeNavigate();

  const handleEdit = () => {
    navigate(
      ROUTES.ADMIN.COURSE_LINES.UNIT(courseLineId, moduleId, slug, unit.id),
      { treeKey: mountKey({ type: 'unit', id: unit.id }) },
    );
  };

  const dragProps = isDraggable
    ? {
        isDraggable: true as const,
        id: mountKey({ type: 'unit', id: unit.id }),
        index,
        disabled: isReordering,
      }
    : { isDraggable: false as const };

  return (
    <CourseAdminCard
      variant="block"
      isDeleting={isDeleting}
      isDragging={isDragging}
      className={twMerge('mb-6', !isDragging && 'pb-0')}
      {...dragProps}
    >
      <CourseAdminCard.Header>
        <CourseAdminCard.Leading>
          <CourseAdminCard.Order value={unit.order} />
          <CourseAdminCard.Title>{unit.name}</CourseAdminCard.Title>
        </CourseAdminCard.Leading>
        <CourseAdminCard.Actions>
          <CourseAdminCard.EditButton onClick={handleEdit} />
          <CourseAdminCard.MoreActions>
            <CourseAdminCard.MoreActions.Delete
              onClick={() => onDelete(unit.id)}
              disabled={isLimitedEdition}
            />
          </CourseAdminCard.MoreActions>
        </CourseAdminCard.Actions>
      </CourseAdminCard.Header>
      <CourseAdminCard.NestedList>
        <NestedLessonsDroppable
          isLoadingLessons={isLoadingLessons}
          unitId={unit.id}
          lessons={lessons}
          isReordering={isReordering}
          courseLineId={courseLineId}
          moduleId={moduleId}
          slug={slug}
        />
      </CourseAdminCard.NestedList>
    </CourseAdminCard>
  );
}

type NestedLessonsDroppableProps = {
  isLoadingLessons: boolean;
  unitId: number;
  lessons: Lesson[];
  isReordering: boolean;
  courseLineId: number;
  moduleId: number;
  slug: string;
};

const NestedLessonsDroppable = ({
  isLoadingLessons,
  unitId,
  lessons,
  isReordering,
  courseLineId,
  moduleId,
  slug,
}: NestedLessonsDroppableProps) => {
  const { isLimitedEdition } = usePedWorkspaceStatus();
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.courseWorkspace.unitsTab',
  });

  const { deleteLesson } = useLessons({ slug, unitId });

  const { navigate } = useCourseTreeNavigate();

  const createRoute = (lessonId: number | string) =>
    ROUTES.ADMIN.COURSE_LINES.LESSON(
      courseLineId,
      moduleId,
      slug,
      unitId,
      lessonId,
    );

  const onClickPlaceholder = () => {
    navigate(createRoute('new'));
  };

  const handleEdit = (lessonId: number) => {
    navigate(createRoute(lessonId), {
      treeKey: mountKey({ type: 'lesson', id: lessonId }),
    });
  };

  const handleDelete = (lessonId: number) => {
    deleteLesson(lessonId);
  };

  return (
    <ConditionalRenderer
      condition={!isLoadingLessons}
      fallback={
        <div className="flex justify-center py-4 mb-6">
          <Loading variant="spinner" size="sm" color="primary" />
        </div>
      }
    >
      <DroppableList
        groupId={String(unitId)}
        type="LESSON"
        className="relative min-h-12"
        placeholder={props => (
          <DragDropPlaceholder
            text={t('dndPlaceholder')}
            className="mb-6 cursor-pointer"
            onClick={onClickPlaceholder}
            {...props}
          />
        )}
        disabled={isLimitedEdition}
      >
        {lessons.map((lesson, lessonIndex) => (
          <LessonCard
            onDelete={handleDelete}
            lesson={lesson}
            isDraggable={true}
            key={lesson.id}
            id={mountKey({ type: 'lesson', id: lesson.id })}
            className="mb-6"
            index={lessonIndex}
            onEdit={handleEdit}
            disabled={isReordering}
          />
        ))}
      </DroppableList>
    </ConditionalRenderer>
  );
};
